import { requestGetJSON } from '../../../NonReact/common/API/REST/RESTAPIClient'
import { INTERNAL_LOGIN_URL, logoutURL, SSO_LOGIN_URL } from '../../../NonReact/constants/ApiConstants'
import { redirectToServlet, ServletCmds } from '../../../NonReact/constants/ServletCommands'
import { displayError } from '../Utils/Messages'
import { requestJSON } from '../../../NonReact/common/API/REST/RESTClient'
import { RESTMethods } from '../../../NonReact/constants/REST'
import { getSession } from './CommonAPI'
import { isVarNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { clearAuthenticated } from '../../../NonReact/utils/SessionUtils'
import { markFusionSignOutRequested } from '../../../NonReact/common/FusionSignOutIntent'
import { RouteID } from '../../../ReactRouting/Components/Molecules/Header/Types/Types'
import { IS_FUSION_LEGACY_INITIAL_URL } from '../../../ReactRouting/Components/Molecules/Header/Constants'
import { APIData, JSONResponse } from '../../../NonReact/common/Types/Types'

const FUSION_LOGOUT_MESSAGE_TYPE = 'akixi:fusion:logout' as const

/**
 * Origin of the Fusion host that embedded this page, captured at module load.
 * In production the parent is same-origin so {@code window.parent.location.origin} is
 * accessible. For cross-origin dev loads the referrer URL is parsed as a fallback.
 * If neither is available the message is skipped rather than using a wildcard target.
 */
const FUSION_HOST_ORIGIN: Nullable<string> = ((): Nullable<string> => {
    try {
        if (window.parent !== window) {
            return window.parent.location.origin
        }
    } catch {
        if (document.referrer) {
            try {
                return new URL(document.referrer).origin
            } catch {
                // Referrer URL could not be parsed.
            }
        }
    }

    return null
})()

/**
 * Mutable flag that tracks whether this CCS instance is embedded as the legacy iframe of the
 * Fusion host. Seeded from {@link IS_FUSION_LEGACY_INITIAL_URL} which covers same-origin prod
 * loads where the URL param or parent-window property is readable at module load.
 *
 * Cross-origin dev loads (CCS on :8084, Fusion host on :8090) cannot read either signal before
 * the server-side redirect strips {@code ?fusionLegacy=true}, so the flag stays {@code false}
 * until the settings API response arrives. {@link confirmFusionLegacyMode} updates it once
 * {@code userSettings.FusionLegacyMode} is known.
 */
let fusionLegacyModeConfirmed: boolean = IS_FUSION_LEGACY_INITIAL_URL

/**
 * Updates the fusion-legacy-mode flag with the server-authoritative value read from
 * {@code userSettings.FusionLegacyMode}.
 *
 * Call this once the user-settings API response is available. The session attribute
 * {@code fusionLegacyMode} is set by {@code CFusionSsoResource} at SSO time and survives
 * the server-side redirect that strips the {@code fusionLegacy} URL param, making it the
 * reliable source of truth for cross-origin iframe loads.
 *
 * @param value - {@code true} when the server confirms fusion-legacy-mode for this session.
 */
export const confirmFusionLegacyMode = (value: boolean): void => {
    fusionLegacyModeConfirmed = value
}

/**
 * Returns whether this CCS instance is embedded as the legacy iframe of the new akixi-frontend
 * (Fusion) host.
 *
 * Uses {@link fusionLegacyModeConfirmed} which is seeded at module-load and updated via
 * {@link confirmFusionLegacyMode} once userSettings arrive, so the correct path is
 * taken whether logout is triggered before or after the settings response.
 *
 * @return {@code true} when this page is the legacy fusion iframe.
 */
const isFusionLegacyMode = (): boolean => fusionLegacyModeConfirmed

/**
 * Notifies the Fusion host that the user signed out inside the legacy iframe so it can
 * tear down its own session and route to the login screen.
 *
 * The message is sent only when the parent origin is known (always the case in
 * production, where CCS and the Fusion host share the same origin). Failures are swallowed
 * because the parent may already be unmounting.
 */
const postFusionLogoutMessage = (): void => {
    if (!FUSION_HOST_ORIGIN) return

    try {
        window.parent.postMessage({ type: FUSION_LOGOUT_MESSAGE_TYPE }, FUSION_HOST_ORIGIN)
    } catch {
        // Best effort.
    }
}

export type LoginResponse = {
    RedirectThePageTo: RouteID
    MainTitle?: string
    PageTitle?: string
    Message?: string
    /** Present and {@code "true"} when the user's Cognito profile has {@code custom:new_ui} set. */
    newUi?: string
}

export type SsoLoginResponse = {
    sessionId: string
    otherSessionsClosed: boolean
}

export const signInWithMsal = (accessToken: string): Promise<JSONResponse<SsoLoginResponse>> =>
    requestGetJSON<JSONResponse<SsoLoginResponse>>(SSO_LOGIN_URL, {
        headers: { 'x-msal-access-token': accessToken }
    })

export const signIn = (username: string, password: string, locale?: string): Promise<Nullable<LoginResponse>> =>
    getSession().then(
        (): Promise<Nullable<LoginResponse>> =>
            requestJSON<Nullable<APIData<LoginResponse>>>(INTERNAL_LOGIN_URL, {
                method: RESTMethods.POST,
                body: {
                    username,
                    plainTextPassword: password,
                    locale
                }
            }).then((response: Nullable<APIData<LoginResponse>>): Nullable<LoginResponse> => response?.Data)
    )

/**
 * Sign out of Akixi.
 *
 * <p>When the CCS is embedded as the legacy iframe of the Fusion host the in-iframe
 * redirect is replaced with a postMessage to the parent so the host owns the transition
 * to the login screen. This avoids the legacy login page rendering inside the iframe
 * before the parent unmounts it.
 *
 * <p>The sign-out is recorded first, so that if this frame lands on the auth page the embedded
 * recovery leaves it there rather than re-establishing the session it has just ended.
 */
export const signOut = (doRedirect?: () => void): void => {
    clearAuthenticated()
    markFusionSignOutRequested()
    if (isFusionLegacyMode()) {
        requestGetJSON(logoutURL)
            .catch((error: Error): void => {
                displayError(error)
            })
            .finally((): void => {
                postFusionLogoutMessage()
            })
        return
    }

    requestGetJSON(logoutURL)
        .then((): void => {
            setTimeout(
                (): void => (isVarNotEmpty(doRedirect) ? doRedirect() : redirectToServlet(ServletCmds.AUTHENTICATION)),
                100
            )
        })
        .catch((error: Error): void => {
            displayError(error)
            setTimeout(
                (): void => (isVarNotEmpty(doRedirect) ? doRedirect() : redirectToServlet(ServletCmds.AUTHENTICATION)),
                1000
            )
        })
}
